import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { currencySymbol } from 'utils/constant';
import { useGetShiftReportQuery } from '../services';

const formatDisplayDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const ShiftDetailModal = ({ open, onClose, shiftId, initialData = null, autoPrint = false }) => {
  const { data: response, isLoading } = useGetShiftReportQuery(shiftId, {
    skip: !shiftId || !open,
  });

  const vendorCurrency = useSelector((state) => state?.posData?.vendor?.currency);
  const currency =
    currencySymbol(vendorCurrency) ||
    (vendorCurrency === 'USD' ? '$' : vendorCurrency === 'EUR' ? '€' : vendorCurrency) ||
    '$';
  const restaurantName =
    useSelector((state) => state?.posData?.vendor?.restaurant_name) || 'Main Branch';

  const reportData = response?.data || initialData || {};
  const shift = reportData.shift || reportData;
  const financials = reportData.financials || reportData;
  const staff = reportData.staff || shift?.staff;
  const closedByStaff = reportData.closed_by_staff || shift?.closed_by_staff;
  const openedAt = reportData.opened_at || shift?.opened_at;
  const closedAt = reportData.closed_at || shift?.closed_at;
  const movements = reportData.movements || [];

  React.useEffect(() => {
    if (autoPrint && open && !isLoading && shift?.id) {
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoPrint, open, isLoading, shift?.id]);

  if (!open) return null;

  const staffName =
    staff?.full_name ||
    (staff?.first_name ? `${staff.first_name} ${staff.last_name || ''}`.trim() : null) ||
    shift?.staff_name ||
    'Staff';

  const closedByName =
    closedByStaff?.full_name ||
    (closedByStaff?.first_name
      ? `${closedByStaff.first_name} ${closedByStaff.last_name || ''}`.trim()
      : null) ||
    shift?.closed_by_name ||
    'Manager';

  const shiftCode =
    shift?.shift_code ||
    `SH-${(shift?.id || shiftId) >= 1000 ? shift?.id || shiftId : 1000 + Number(shift?.id || shiftId || 0)}`;
  const shiftName = shift?.shift_name || 'Morning Shift';
  const tillName = shift?.till_name || 'Till 1';

  const openFloat = parseFloat(financials?.opening_float || shift?.opening_float || 0);
  const cashSales = parseFloat(financials?.cash_sales || shift?.cash_sales || 0);
  const cashIn = parseFloat(
    financials?.cash_in ||
      financials?.cash_in_total ||
      shift?.cash_in ||
      shift?.cash_in_total ||
      0
  );
  const cashOut = parseFloat(
    financials?.cash_out ||
      financials?.cash_out_total ||
      shift?.cash_out ||
      shift?.cash_out_total ||
      0
  );
  const expectedCash = parseFloat(financials?.expected_cash || shift?.expected_cash || 0);
  const actualCash =
    financials?.actual_cash != null
      ? parseFloat(financials.actual_cash)
      : shift?.actual_cash != null
      ? parseFloat(shift.actual_cash)
      : null;
  const variance = parseFloat(
    financials?.cash_variance ??
      financials?.variance ??
      shift?.variance ??
      shift?.cash_variance ??
      0
  );
  const cashRemoval = parseFloat(financials?.cash_removal || shift?.cash_removal || 0);
  const closingFloat = parseFloat(financials?.closing_float || shift?.closing_float || 0);
  const discrepancyReason = financials?.discrepancy_reason || shift?.discrepancy_reason;

  let varianceLabel = `${currency}0.00`;
  if (variance > 0) {
    varianceLabel = `+${currency}${variance.toFixed(2)}`;
  } else if (variance < 0) {
    varianceLabel = `-${currency}${Math.abs(variance).toFixed(2)}`;
  }

  const hasData = !isLoading || initialData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '28px 32px 32px 32px',
          width: '100%',
          maxWidth: '720px',
          boxShadow: '0px 10px 35px rgba(0, 0, 0, 0.15)',
          position: 'relative',
        },
        className: 'printable-shift-modal',
      }}
    >
      {/* Top Close Button (X in circle) */}
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 24,
          top: 24,
          color: '#4B5563',
          border: '1.5px solid #9CA3AF',
          width: 32,
          height: 32,
          padding: 0,
          borderRadius: '50%',
          '&:hover': {
            bgcolor: '#F3F4F6',
            borderColor: '#4B5563',
            color: '#111827',
          },
        }}
        className="no-print"
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Header: Title & Subtitle */}
      <Box sx={{ pr: 6, mb: 3.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '22px',
            color: '#111827',
            letterSpacing: '-0.3px',
            lineHeight: 1.25,
          }}
        >
          {shiftName} · {shiftCode}
        </Typography>
        <Typography
          sx={{
            color: '#6B7280',
            fontSize: '14px',
            mt: 0.6,
            fontWeight: 400,
          }}
        >
          {restaurantName} · {tillName} · Read-only
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {!hasData ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={4}>
            {/* ══════════ LEFT COLUMN: FINANCIAL SUMMARY ══════════ */}
            <Grid item xs={12} sm={6.8}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* Opened */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Opened</Typography>
                  <Typography
                    sx={{ color: '#111827', fontSize: '14px', fontWeight: 500, textAlign: 'right' }}
                  >
                    {staffName} · {formatDisplayDate(openedAt)}
                  </Typography>
                </Box>

                {/* Closed */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Closed</Typography>
                  <Typography
                    sx={{ color: '#111827', fontSize: '14px', fontWeight: 500, textAlign: 'right' }}
                  >
                    {closedAt ? `${closedByName} · ${formatDisplayDate(closedAt)}` : 'Still open'}
                  </Typography>
                </Box>

                <Box sx={{ height: 16 }} />

                {/* Opening Float */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Opening Float</Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    {currency}
                    {openFloat.toFixed(2)}
                  </Typography>
                </Box>

                {/* Cash Sales */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Cash Sales</Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    {currency}
                    {cashSales.toFixed(2)}
                  </Typography>
                </Box>

                {/* Cash In */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Cash In</Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    {currency}
                    {cashIn.toFixed(2)}
                  </Typography>
                </Box>

                {/* Cash Out */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Cash Out</Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    {currency}
                    {cashOut.toFixed(2)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1.5, borderColor: '#E5E7EB' }} />

                {/* Expected */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#111827', fontSize: '14.5px', fontWeight: 700 }}>
                    Expected
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14.5px', fontWeight: 700 }}>
                    {currency}
                    {expectedCash.toFixed(2)}
                  </Typography>
                </Box>

                {/* Actual */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Actual</Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    {actualCash != null ? `${currency}${actualCash.toFixed(2)}` : '-'}
                  </Typography>
                </Box>

                {/* Variance */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Variance</Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    {varianceLabel}
                  </Typography>
                </Box>

                {/* Reason */}
                {(discrepancyReason || variance !== 0) && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.35,
                    }}
                  >
                    <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Reason</Typography>
                    <Typography
                      sx={{ color: '#111827', fontSize: '14px', fontWeight: 500, textAlign: 'right' }}
                    >
                      {discrepancyReason || 'Counting Error'}
                    </Typography>
                  </Box>
                )}

                {/* Removed */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>Removed</Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14px', fontWeight: 500 }}>
                    {currency}
                    {cashRemoval.toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ height: 16 }} />

                {/* Closing Float */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.35,
                  }}
                >
                  <Typography sx={{ color: '#111827', fontSize: '14.5px', fontWeight: 700 }}>
                    Closing Float
                  </Typography>
                  <Typography sx={{ color: '#111827', fontSize: '14.5px', fontWeight: 700 }}>
                    {currency}
                    {closingFloat.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* ══════════ RIGHT COLUMN: AUDIT TRAIL ══════════ */}
            <Grid item xs={12} sm={5.2}>
              <Box sx={{ pl: { sm: 1 }, height: '100%' }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '14.5px',
                    color: '#111827',
                    mb: 1.8,
                  }}
                >
                  Audit trail
                </Typography>

                <Box
                  sx={{
                    borderLeft: '1.5px solid #E5E7EB',
                    pl: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.8,
                  }}
                >
                  {/* Step 1: Shift opened */}
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                      Shift opened
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#4B5563', mt: 0.3 }}>
                      Opening float {currency}
                      {openFloat.toFixed(2)}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.3 }}>
                      {staffName} · {formatDisplayDate(openedAt)}
                    </Typography>
                  </Box>

                  {/* Movements (if any manual Cash In / Cash Out) */}
                  {movements.map((m) => {
                    const isCashIn = m.movement_type === 'cash_in';
                    const mAmount = parseFloat(m.amount || 0);
                    return (
                      <Box key={m.id}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                          {isCashIn ? 'Cash in' : 'Cash out'}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#4B5563', mt: 0.3 }}>
                          {isCashIn ? '+' : '-'}
                          {currency}
                          {mAmount.toFixed(2)}
                          {m.reason ? ` - ${m.reason}` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.3 }}>
                          {staffName} · {formatDisplayDate(m.created_at)}
                        </Typography>
                      </Box>
                    );
                  })}

                  {/* Step 2: Shift closed */}
                  {closedAt && (
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                        Shift closed
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#4B5563', mt: 0.3 }}>
                        Actual {currency}
                        {actualCash != null ? actualCash.toFixed(2) : '0.00'}, removed {currency}
                        {cashRemoval.toFixed(2)}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.3 }}>
                        {closedByName} · {formatDisplayDate(closedAt)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShiftDetailModal;
